import React, { useState } from 'react';
import { ConceptWikiNode } from '../types/paper';
import { MathView } from './MathView';
import { BookOpen, Code, Cpu, Sigma, Lightbulb, GitCommit, Layers, Search } from 'lucide-react';

interface ConceptWikiProps {
  concepts: ConceptWikiNode[];
  paperTitle: string;
}

export const ConceptWiki: React.FC<ConceptWikiProps> = ({ concepts, paperTitle }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeConceptId, setActiveConceptId] = useState<string>(concepts[0]?.id || '');

  const categories = [
    { id: 'all', label: 'All Concepts', icon: BookOpen },
    { id: 'core_innovation', label: 'Core Innovations', icon: Lightbulb },
    { id: 'architectural_component', label: 'Architecture', icon: Cpu },
    { id: 'mathematical_formulation', label: 'Math Formulas', icon: Sigma },
  ];

  const filteredConcepts = concepts.filter(c => {
    const matchesCategory = selectedCategory === 'all' || c.category === selectedCategory;
    const matchesQuery = c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.definition.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.intuition.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesQuery;
  });

  const activeConcept = concepts.find(c => c.id === activeConceptId) || filteredConcepts[0] || concepts[0];

  return (
    <div className="glass-panel rounded-2xl p-6 border border-slate-800 shadow-2xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-2 text-cyan-400 text-sm font-semibold uppercase tracking-wider">
            <BookOpen className="w-4 h-4" />
            LLM-Wiki Knowledge Base
          </div>
          <h2 className="text-2xl font-bold text-white">
            Derived Concepts & Mathematical Foundations
          </h2>
        </div>

        {/* Search Bar */}
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search concepts, math, terms..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full bg-dark-card/90 border border-slate-700/80 rounded-xl pl-10 pr-4 py-2 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-all"
          />
        </div>
      </div>

      {/* Main Layout: Left Sidebar + Right Content Drawer */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Concept Selector */}
        <div className="lg:col-span-4 space-y-4">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-1.5 p-1 bg-dark-bg/80 rounded-xl border border-slate-800">
            {categories.map(cat => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-brand-600 text-white shadow-glow-blue'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* List of Concepts */}
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
            {filteredConcepts.map(c => (
              <button
                key={c.id}
                onClick={() => setActiveConceptId(c.id)}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  activeConcept?.id === c.id
                    ? 'bg-brand-900/40 border-brand-500/60 shadow-glow-blue text-white'
                    : 'bg-dark-card/60 border-slate-800/80 text-slate-300 hover:bg-dark-card hover:border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-bold text-sm text-white">{c.title}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-cyan-300 font-mono border border-slate-700">
                    {c.category.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">
                  {c.definition}
                </p>
              </button>
            ))}
            {filteredConcepts.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">
                No concepts found matching filter.
              </div>
            )}
          </div>
        </div>

        {/* Right Active Concept Content Reader */}
        <div className="lg:col-span-8">
          {activeConcept ? (
            <div className="bg-dark-card/80 border border-slate-800 rounded-2xl p-6 space-y-6 text-slate-200">
              
              {/* Concept Title & Category */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-brand-900/60 text-brand-300 border border-brand-500/40 font-mono">
                    {activeConcept.category.toUpperCase()}
                  </span>
                </div>
                <h3 className="text-2xl font-extrabold text-white">{activeConcept.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed mt-2 p-3 bg-dark-bg/60 rounded-xl border border-slate-800">
                  {activeConcept.definition}
                </p>
              </div>

              {/* Math Formulas Section */}
              {activeConcept.mathFormulas.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-cyan-400 flex items-center gap-2">
                    <Sigma className="w-4 h-4" /> Mathematical Derivations & Formulas
                  </h4>
                  <div className="space-y-3">
                    {activeConcept.mathFormulas.map((f, i) => (
                      <div key={i} className="p-4 bg-dark-bg/90 rounded-xl border border-slate-800/90 space-y-2">
                        <div className="text-xs font-semibold text-slate-400">{f.label}</div>
                        <div className="py-2 px-3 bg-slate-950/80 rounded-lg overflow-x-auto text-center border border-slate-800">
                          <MathView math={f.latex} block={true} className="text-brand-300 text-base" />
                        </div>
                        <p className="text-xs text-slate-300 leading-relaxed">{f.explanation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Intuition Section */}
              <div className="space-y-2">
                <h4 className="text-sm font-bold uppercase tracking-wider text-amber-400 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" /> Core Intuition & Mechanics
                </h4>
                <p className="text-sm text-slate-300 leading-relaxed p-4 bg-amber-950/20 rounded-xl border border-amber-500/20">
                  {activeConcept.intuition}
                </p>
              </div>

              {/* Key Nuances */}
              {activeConcept.keyNuances.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-slate-300">
                    Key Nuances & Technical Edge Cases
                  </h4>
                  <ul className="list-disc list-inside space-y-1.5 text-xs text-slate-300">
                    {activeConcept.keyNuances.map((n, i) => (
                      <li key={i} className="leading-relaxed">{n}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Code Implementation */}
              {activeConcept.codeSnippet && (
                <div className="space-y-2">
                  <h4 className="text-sm font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-2">
                    <Code className="w-4 h-4" /> Python / PyTorch Pseudocode
                  </h4>
                  <div className="bg-slate-950 rounded-xl border border-slate-800 overflow-hidden font-mono text-xs">
                    <div className="px-4 py-2 bg-slate-900 border-b border-slate-800 text-slate-400 font-semibold">
                      {activeConcept.codeSnippet.language}
                    </div>
                    <pre className="p-4 overflow-x-auto text-emerald-300 leading-relaxed">
                      <code>{activeConcept.codeSnippet.code}</code>
                    </pre>
                  </div>
                  <p className="text-xs text-slate-400 italic">{activeConcept.codeSnippet.explanation}</p>
                </div>
              )}

              {/* Lineage & Ancestry Connections */}
              {activeConcept.ancestors.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-slate-800">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-purple-400 flex items-center gap-1.5">
                    <GitCommit className="w-3.5 h-3.5" /> Ancestors & Influences
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {activeConcept.ancestors.map((a, i) => (
                      <div key={i} className="p-3 bg-dark-bg/80 rounded-xl border border-slate-800 text-xs">
                        <div className="font-semibold text-slate-200">{a.concept}</div>
                        <div className="text-[11px] text-purple-300 font-mono mb-1">{a.paperTitle} ({a.authorsYear})</div>
                        <div className="text-slate-400">{a.relationship}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              Select a concept from the left panel to inspect its mathematical derivations.
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
